import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import SidebarThemeContext from './SidebarThemeContext';
import { getBackgroundStyle, getOverLayStyle } from '../../CmtHelpers/JssHelper';
import AppContext from '../../../@jumbo/components/contextProvider/AppContextProvider/AppContext';
import themeSidebarColor from './sidebarThemeColors';

const darkThemeSetting = {
  textColor: 'rgba(255, 255, 255, 0.7)',
  textDarkColor: '#fff',
  textActiveColor: '#fff',
  navHoverBgColor: '#aace3d',
  navActiveBgColor: 'green',
  borderColor: 'rgba(255, 255, 255, 0.2)',
};

const getStoredCertificationThemeMode = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = window.localStorage.getItem('certificationThemeMode');
  if (stored === 'dark' || stored === 'light') {
    return stored;
  }
  return null;
};

const SidebarThemeProvider = ({ children }) => {
  const { themeType } = useContext(AppContext);
  const location = useLocation();
  const currentPath = location?.pathname?.toLowerCase?.() || '';
  const isCertToolRoute = currentPath.startsWith('/dealer/certification');
  const [certThemeMode, setCertThemeMode] = useState(() => getStoredCertificationThemeMode());
  const resolvedThemeType = isCertToolRoute ? certThemeMode || themeType : themeType;
  const resolvedTheme = themeSidebarColor[resolvedThemeType] || themeSidebarColor.light;
  const [activeTheme, setActiveTheme] = useState({
    ...resolvedTheme,
    backgroundColor: '',
    backgroundImage: '',
    gradientDirection: '',
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncMode = () => {
      setCertThemeMode(getStoredCertificationThemeMode());
    };

    syncMode();
    window.addEventListener('cert-theme-mode-changed', syncMode);
    window.addEventListener('storage', syncMode);

    return () => {
      window.removeEventListener('cert-theme-mode-changed', syncMode);
      window.removeEventListener('storage', syncMode);
    };
  }, []);

  useEffect(() => {
    if (activeTheme.backgroundImage) {
      if (!activeTheme.backgroundColor) {
        setActiveTheme({
          ...activeTheme,
          ...darkThemeSetting,
          backgroundColor: '#000',
        });
      }
    } else {
      //this means no flat and gradient style and no background image
      if (activeTheme.backgroundColor === '#000') {
        setActiveTheme({
          ...activeTheme,
          ...resolvedTheme,
          backgroundColor: '',
        });
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTheme.backgroundImage]);

  useEffect(() => {
    if (!activeTheme.backgroundColor && !activeTheme.backgroundImage) {
      setActiveTheme({
        ...resolvedTheme,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedThemeType]);

  const sidebarContextValue = useMemo(() => {
    const backgroundStyle = getBackgroundStyle(
      activeTheme.backgroundColor,
      activeTheme.backgroundImage,
      activeTheme.gradientDirection,
    );
    const overlayStyle = getOverLayStyle({
      colors: activeTheme.backgroundColor,
      opacity: 0.6,
      direction: '180deg',
    });

    return {
      sidebarTheme: activeTheme,
      backgroundStyle: backgroundStyle,
      overlayStyle: overlayStyle,
      setSidebarTheme: setActiveTheme,
    };
  }, [activeTheme]);

  return <SidebarThemeContext.Provider value={sidebarContextValue}>{children}</SidebarThemeContext.Provider>;
};

export default SidebarThemeProvider;
